// Transaction scenario fixtures for testing and development
// Each scenario is self-contained with transaction metadata and ledger rows

import type { Transaction, LedgerRow } from './transactionViewModel'
import { getRowNetImpact } from './transactionViewModel'

export interface TransactionScenario {
  scenarioId: string
  description: string
  transaction: Transaction
  ledgerRows: LedgerRow[]
}

// Normalize ledger rows to ensure settlement.amountInBucket matches computed net impact
// This prevents fixtures from having inconsistent amountInBucket values
function normalizeLedgerRows(rows: LedgerRow[]): LedgerRow[] {
  return rows.map((row) => {
    if (!row.settlement) return row

    // Override amountInBucket with computed net impact for consistency
    return {
      ...row,
      settlement: {
        ...row.settlement,
        amountInBucket: getRowNetImpact(row),
      },
    }
  })
}

// Scenario 1: Payment only + reserve hold
const SCENARIO_PAYMENT_WITH_RESERVE: TransactionScenario = {
  scenarioId: 'payment-with-reserve',
  description: 'Simple payment with 20% reserve hold',
  transaction: {
    id: '1',
    transactionId: 'TXN-2025-001',
    amount: 500,
    currency: 'THB',
    date: '2025-12-01T10:00:00',
  },
  ledgerRows: [
    {
      id: 'L1',
      posting: 'Payment captured',
      attemptId: null,
      type: 'payment',
      gross: 500,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '01/12/2025 10:00',
      settlement: {
        kind: 'payout',
        id: 'po_10001',
        date: '03/12/2025',
        status: 'Scheduled',
        amountInBucket: 500,
      },
      feeBreakdown: null,
    },
    {
      id: 'L2',
      posting: 'Gateway fee',
      attemptId: null,
      type: 'fee',
      gross: null,
      feeExTax: -5,
      taxAmount: -0.4, // 8% SST
      status: 'Posted',
      effectiveAt: '01/12/2025 10:01',
      settlement: {
        kind: 'payout',
        id: 'po_10001',
        date: '03/12/2025',
        status: 'Scheduled',
        amountInBucket: -5.4,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [
          { label: 'Gateway processing fee', amountExTax: -5, taxAmount: -0.4 },
        ],
      },
    },
    {
      id: 'L3',
      posting: 'Reserve hold (20% / 30 days)',
      attemptId: null,
      type: 'reserve_hold',
      gross: null,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '01/12/2025 10:02',
      settlement: {
        kind: 'reserve',
        id: 'rv_001',
        releaseDate: '31/12/2025',
        status: 'Active',
        amountInBucket: -100,
      },
      feeBreakdown: null,
    },
    {
      id: 'L4',
      posting: 'Reserve release',
      attemptId: null,
      type: 'reserve_release',
      gross: null,
      feeExTax: null,
      taxAmount: null,
      status: 'Scheduled',
      effectiveAt: '31/12/2025 00:00',
      settlement: {
        kind: 'reserve',
        id: 'rv_001',
        releaseDate: '31/12/2025',
        status: 'Scheduled',
        amountInBucket: 100,
      },
      feeBreakdown: null,
    },
  ],
}

// Scenario 2: Payment + reserve hold + partial refund
const SCENARIO_PARTIAL_REFUND: TransactionScenario = {
  scenarioId: 'partial-refund',
  description: 'Payment with reserve, then partial refund',
  transaction: {
    id: '2',
    transactionId: 'TXN-2025-002',
    amount: 800,
    currency: 'THB',
    date: '2025-12-01T14:00:00',
  },
  ledgerRows: [
    {
      id: 'L1',
      posting: 'Payment captured',
      attemptId: null,
      type: 'payment',
      gross: 800,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '01/12/2025 14:00',
      settlement: {
        kind: 'payout',
        id: 'po_10002',
        date: '03/12/2025',
        status: 'Scheduled',
        amountInBucket: 800,
      },
      feeBreakdown: null,
    },
    {
      id: 'L2',
      posting: 'Gateway fee',
      attemptId: null,
      type: 'fee',
      gross: null,
      feeExTax: -8,
      taxAmount: -0.64, // 8% SST
      status: 'Posted',
      effectiveAt: '01/12/2025 14:01',
      settlement: {
        kind: 'payout',
        id: 'po_10002',
        date: '03/12/2025',
        status: 'Scheduled',
        amountInBucket: -8.64,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [
          {
            label: 'Gateway processing fee',
            amountExTax: -8,
            taxAmount: -0.64,
          },
        ],
      },
    },
    {
      id: 'L3',
      posting: 'Reserve hold (20% / 30 days)',
      attemptId: null,
      type: 'reserve_hold',
      gross: null,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '01/12/2025 14:02',
      settlement: {
        kind: 'reserve',
        id: 'rv_002',
        releaseDate: '31/12/2025',
        status: 'Active',
        amountInBucket: -160,
      },
      feeBreakdown: null,
    },
    {
      id: 'L4',
      posting: 'Partial refund',
      attemptId: null,
      type: 'refund',
      gross: -300,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '02/12/2025 10:30',
      settlement: {
        kind: 'payout',
        id: 'po_10003',
        date: '04/12/2025',
        status: 'Scheduled',
        amountInBucket: -300,
      },
      feeBreakdown: null,
    },
    {
      id: 'L5',
      posting: 'Reserve release',
      attemptId: null,
      type: 'reserve_release',
      gross: null,
      feeExTax: null,
      taxAmount: null,
      status: 'Scheduled',
      effectiveAt: '31/12/2025 00:00',
      settlement: {
        kind: 'reserve',
        id: 'rv_002',
        releaseDate: '31/12/2025',
        status: 'Scheduled',
        amountInBucket: 160,
      },
      feeBreakdown: null,
    },
  ],
}

// Scenario 3: Payment + full refund
const SCENARIO_FULL_REFUND: TransactionScenario = {
  scenarioId: 'full-refund',
  description: 'Payment fully refunded',
  transaction: {
    id: '3',
    transactionId: 'TXN-2025-003',
    amount: 1200,
    currency: 'THB',
    date: '2025-12-05T09:00:00',
  },
  ledgerRows: [
    {
      id: 'L1',
      posting: 'Payment captured',
      attemptId: null,
      type: 'payment',
      gross: 1200,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '05/12/2025 09:00',
      settlement: {
        kind: 'payout',
        id: 'po_10004',
        date: '07/12/2025',
        status: 'Scheduled',
        amountInBucket: 1200,
      },
      feeBreakdown: null,
    },
    {
      id: 'L2',
      posting: 'Gateway fee',
      attemptId: null,
      type: 'fee',
      gross: null,
      feeExTax: -12,
      taxAmount: -0.96, // 8% SST
      status: 'Posted',
      effectiveAt: '05/12/2025 09:01',
      settlement: {
        kind: 'payout',
        id: 'po_10004',
        date: '07/12/2025',
        status: 'Scheduled',
        amountInBucket: -12.96,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [
          {
            label: 'Gateway processing fee',
            amountExTax: -12,
            taxAmount: -0.96,
          },
        ],
      },
    },
    {
      id: 'L3',
      posting: 'Interchange fee',
      attemptId: null,
      type: 'fee',
      gross: null,
      feeExTax: -18,
      taxAmount: -1.44, // 8% SST
      status: 'Posted',
      effectiveAt: '06/12/2025 08:00',
      settlement: {
        kind: 'payout',
        id: 'po_10005',
        date: '08/12/2025',
        status: 'Scheduled',
        amountInBucket: -19.44,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [{ label: 'Interchange', amountExTax: -18, taxAmount: -1.44 }],
      },
    },
    {
      id: 'L4',
      posting: 'Full refund',
      attemptId: null,
      type: 'refund',
      gross: -1200,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '06/12/2025 15:00',
      settlement: {
        kind: 'payout',
        id: 'po_10006',
        date: '08/12/2025',
        status: 'Scheduled',
        amountInBucket: -1200,
      },
      feeBreakdown: null,
    },
  ],
}

// Scenario 4: Payment + partial dispute
const SCENARIO_PARTIAL_DISPUTE: TransactionScenario = {
  scenarioId: 'partial-dispute',
  description: 'Payment with partial dispute (not yet resolved)',
  transaction: {
    id: '4',
    transactionId: 'TXN-2025-004',
    amount: 2000,
    currency: 'THB',
    date: '2025-11-28T11:00:00',
  },
  ledgerRows: [
    {
      id: 'L1',
      posting: 'Payment captured',
      attemptId: null,
      type: 'payment',
      gross: 2000,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '28/11/2025 11:00',
      settlement: {
        kind: 'payout',
        id: 'po_10007',
        date: '30/11/2025',
        status: 'Scheduled',
        amountInBucket: 2000,
      },
      feeBreakdown: null,
    },
    {
      id: 'L2',
      posting: 'Gateway fee',
      attemptId: null,
      type: 'fee',
      gross: null,
      feeExTax: -20,
      taxAmount: -1.6, // 8% SST
      status: 'Posted',
      effectiveAt: '28/11/2025 11:01',
      settlement: {
        kind: 'payout',
        id: 'po_10007',
        date: '30/11/2025',
        status: 'Scheduled',
        amountInBucket: -21.6,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [
          {
            label: 'Gateway processing fee',
            amountExTax: -20,
            taxAmount: -1.6,
          },
        ],
      },
    },
    {
      id: 'L3',
      posting: 'Interchange fee',
      attemptId: null,
      type: 'fee',
      gross: null,
      feeExTax: -30,
      taxAmount: -2.4, // 8% SST
      status: 'Posted',
      effectiveAt: '29/11/2025 09:00',
      settlement: {
        kind: 'payout',
        id: 'po_10008',
        date: '01/12/2025',
        status: 'Scheduled',
        amountInBucket: -32.4,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [{ label: 'Interchange', amountExTax: -30, taxAmount: -2.4 }],
      },
    },
    {
      id: 'L4',
      posting: 'Reserve hold (20% / 30 days)',
      attemptId: null,
      type: 'reserve_hold',
      gross: null,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '28/11/2025 11:02',
      settlement: {
        kind: 'reserve',
        id: 'rv_004',
        releaseDate: '28/12/2025',
        status: 'Active',
        amountInBucket: -400,
      },
      feeBreakdown: null,
    },
    {
      id: 'L5',
      posting: 'Dispute received',
      attemptId: null,
      type: 'dispute',
      gross: -600,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '03/12/2025 14:00',
      settlement: {
        kind: 'payout',
        id: 'po_10009',
        date: '05/12/2025',
        status: 'Scheduled',
        amountInBucket: -600,
      },
      feeBreakdown: null,
    },
    {
      id: 'L6',
      posting: 'Dispute fee',
      attemptId: null,
      type: 'fee',
      gross: null,
      feeExTax: -50,
      taxAmount: -4, // 8% SST
      status: 'Posted',
      effectiveAt: '03/12/2025 14:01',
      settlement: {
        kind: 'payout',
        id: 'po_10009',
        date: '05/12/2025',
        status: 'Scheduled',
        amountInBucket: -54,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [
          { label: 'Dispute handling fee', amountExTax: -50, taxAmount: -4 },
        ],
      },
    },
    {
      id: 'L7',
      posting: 'Reserve release',
      attemptId: null,
      type: 'reserve_release',
      gross: null,
      feeExTax: null,
      taxAmount: null,
      status: 'Scheduled',
      effectiveAt: '28/12/2025 00:00',
      settlement: {
        kind: 'reserve',
        id: 'rv_004',
        releaseDate: '28/12/2025',
        status: 'Scheduled',
        amountInBucket: 400,
      },
      feeBreakdown: null,
    },
  ],
}

// Scenario 5: Complex scenario (original) - Payment + reserve + partial refund + dispute won
const SCENARIO_COMPLEX: TransactionScenario = {
  scenarioId: 'complex-dispute-won',
  description:
    'Complex: payment, reserve, partial refund, dispute received and won',
  transaction: {
    id: '5',
    transactionId: 'TXN-2025-005',
    amount: 1000,
    currency: 'THB',
    date: '2025-12-01T10:00:00',
  },
  ledgerRows: [
    {
      id: 'L1',
      posting: 'Payment captured',
      attemptId: null,
      type: 'payment',
      gross: 1000,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '01/12/2025 10:00',
      settlement: {
        kind: 'payout',
        id: 'po_12345',
        date: '03/12/2025',
        status: 'Scheduled',
        amountInBucket: 1000,
      },
      feeBreakdown: null,
    },
    {
      id: 'L2',
      posting: 'Gateway fee',
      attemptId: null,
      type: 'fee',
      gross: null,
      feeExTax: -1,
      taxAmount: -0.08,
      status: 'Posted',
      effectiveAt: '01/12/2025 10:01',
      settlement: {
        kind: 'payout',
        id: 'po_12345',
        date: '03/12/2025',
        status: 'Scheduled',
        amountInBucket: -1.08,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [
          {
            label: 'Gateway processing fee',
            amountExTax: -1,
            taxAmount: -0.08,
          },
        ],
      },
    },
    {
      id: 'L3',
      posting: 'Reserve hold (20% / 30 days)',
      attemptId: null,
      type: 'reserve_hold',
      gross: null,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '01/12/2025 10:02',
      settlement: {
        kind: 'reserve',
        id: 'rv_001',
        releaseDate: '31/12/2025',
        status: 'Active',
        amountInBucket: -200,
      },
      feeBreakdown: null,
    },
    {
      id: 'L4',
      posting: 'Partial refund',
      attemptId: null,
      type: 'refund',
      gross: -200,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '02/12/2025 14:30',
      settlement: {
        kind: 'payout',
        id: 'po_12346',
        date: '04/12/2025',
        status: 'Scheduled',
        amountInBucket: -200,
      },
      feeBreakdown: null,
    },
    {
      id: 'L5',
      posting: 'Interchange fee',
      attemptId: null,
      type: 'fee',
      gross: null,
      feeExTax: -15,
      taxAmount: -1.2,
      status: 'Posted',
      effectiveAt: '03/12/2025 09:00',
      settlement: {
        kind: 'payout',
        id: 'po_12347',
        date: '05/12/2025',
        status: 'Scheduled',
        amountInBucket: -16.2,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [{ label: 'Interchange', amountExTax: -15, taxAmount: -1.2 }],
      },
    },
    {
      id: 'L6',
      posting: 'Scheme fee',
      attemptId: null,
      type: 'fee',
      gross: null,
      feeExTax: -10,
      taxAmount: -0.8,
      status: 'Posted',
      effectiveAt: '03/12/2025 09:01',
      settlement: {
        kind: 'payout',
        id: 'po_12347',
        date: '05/12/2025',
        status: 'Scheduled',
        amountInBucket: -10.8,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [
          { label: 'Card scheme fee', amountExTax: -10, taxAmount: -0.8 },
        ],
      },
    },
    {
      id: 'L7',
      posting: 'Dispute received',
      attemptId: null,
      type: 'dispute',
      gross: -400,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '05/12/2025 11:00',
      settlement: {
        kind: 'payout',
        id: 'po_12348',
        date: '07/12/2025',
        status: 'Scheduled',
        amountInBucket: -400,
      },
      feeBreakdown: null,
    },
    {
      id: 'L8',
      posting: 'Dispute fee',
      attemptId: null,
      type: 'fee',
      gross: null,
      feeExTax: -45,
      taxAmount: -3.6,
      status: 'Posted',
      effectiveAt: '05/12/2025 11:01',
      settlement: {
        kind: 'payout',
        id: 'po_12348',
        date: '07/12/2025',
        status: 'Scheduled',
        amountInBucket: -48.6,
      },
      feeBreakdown: {
        jurisdiction: 'MY (SST 8%)',
        items: [
          { label: 'Dispute handling fee', amountExTax: -45, taxAmount: -3.6 },
        ],
      },
    },
    {
      id: 'L9',
      posting: 'Dispute won - funds returned',
      attemptId: null,
      type: 'dispute_reversal',
      gross: 400,
      feeExTax: null,
      taxAmount: null,
      status: 'Posted',
      effectiveAt: '13/12/2025 16:00',
      settlement: {
        kind: 'payout',
        id: 'po_12349',
        date: '15/12/2025',
        status: 'Scheduled',
        amountInBucket: 400,
      },
      feeBreakdown: null,
    },
    {
      id: 'L10',
      posting: 'Reserve release',
      attemptId: null,
      type: 'reserve_release',
      gross: null,
      feeExTax: null,
      taxAmount: null,
      status: 'Scheduled',
      effectiveAt: '31/12/2025 00:00',
      settlement: {
        kind: 'reserve',
        id: 'rv_001',
        releaseDate: '31/12/2025',
        status: 'Scheduled',
        amountInBucket: 200,
      },
      feeBreakdown: null,
    },
  ],
}

// All scenarios
export const SCENARIOS: TransactionScenario[] = [
  SCENARIO_PAYMENT_WITH_RESERVE,
  SCENARIO_PARTIAL_REFUND,
  SCENARIO_FULL_REFUND,
  SCENARIO_PARTIAL_DISPUTE,
  SCENARIO_COMPLEX,
]

// Helper to get a scenario by ID
export function buildScenario(scenarioId: string): TransactionScenario {
  const scenario = SCENARIOS.find((s) => s.scenarioId === scenarioId)
  if (!scenario) {
    throw new Error(`Scenario not found: ${scenarioId}`)
  }
  // Normalize ledger rows to ensure consistency
  return {
    ...scenario,
    ledgerRows: normalizeLedgerRows(scenario.ledgerRows),
  }
}

// Helper to get scenario by index (for easy switching)
export function getScenarioByIndex(index: number): TransactionScenario {
  if (index < 0 || index >= SCENARIOS.length) {
    throw new Error(`Invalid scenario index: ${index}`)
  }
  return SCENARIOS[index]
}

// Default scenario (complex one for demo)
export const DEFAULT_SCENARIO_ID = 'complex-dispute-won'
